import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import Scrollbar from "react-scrollbars-custom";
import { Button, IconButton, StyledEngineProvider } from "@mui/material";
import DoubleArrowIcon from "@mui/icons-material/DoubleArrow";
import {
  DifferenceDocument,
  PageParts,
} from "../../components/differenceDocument/DifferenceDocument";
import PageWrapper from "../../components/pageWrapper/PageWeapper";
import styles from "./DifferencePage.module.scss";
import { PopupElement } from "../../ducks/popup";
import {
  IDetailedDocument,
  IDifference,
  IDiscrepancyStatus,
  IDocumentImage,
  ITechReport,
  IUpdateDocument,
} from "../../api/monitoringApi";
import { truncate } from "../../helper/other";
import { MonitoringStatuses } from "../../ducks/montitoring";
import { useDraggable } from "react-use-draggable-scroll";
import { DocumentDifferenceLoaderConnected } from "../../components/urlLoader/DocumentDifferenceLoaderConnected";
import { DifferenceListConnected } from "../../components/differenceList/DifferenceListConnected";
import { useSelector } from "react-redux";
import { getActInfoConditional } from "../../selectors/monitoring";
import { selectDiscrepancyFilters } from "../../selectors/monitoring";
import moment from "moment";

export interface IDifferencePageStateProps {
  title: string;
  comment: string;
  report: string;
  reportPackageId: number[];
  packageId: any;
  detailedDiff: string | undefined;
  detailedDocLeftId: string | undefined;
  detailedDocLeft: IDetailedDocument | undefined;
  detailedDocRight: IDetailedDocument | undefined;
  detailedDocRightId: string | undefined;
  isTrackDisabled: boolean;
  imagesLeft: IDocumentImage[];
  imagesRight: IDocumentImage[];
  leftScale: number;
  rightScale: number;
  docStatus: MonitoringStatuses;
}

export interface IDifferencePageDispatchProps {
  setDetailedDiff: (diff: string | undefined) => void;
  popupPush: (popup: PopupElement) => void;
  onClearDifferencePage: () => void;
  routeTo: (route: string | null, id?: number) => void;
  updateDocumentStatus: (
    documentId: string,
    props: IUpdateDocument,
    helpForm?: boolean
  ) => void;
  setDifferenceStatus: (index: number, status: IDiscrepancyStatus) => void;
  deleteUnrecognizedPackagesImages: (packageID: number) => void;
  setLeftScale: (leftScale: number) => void;
  setRightScale: (rightScale: number) => void;
  setIsTrackDisabled: (flag: boolean) => void;
  sendTechnicalReport: (props: ITechReport) => void;
  updateTechReportText: (value: string) => void;
}

interface IDifferenceListProps {
  images: IDocumentImage[];
  detailedDoc: IDetailedDocument | undefined;
  detailedDocId: string;
  scale: number;
  detailedDiff: string | undefined;
  setDetailedDiff: (diff: string | undefined) => void;
  setScale: (scale: number) => void;
  zoom: number;
  rotate: number;
  onLoad?: () => void;
  side: PageParts;
  docContRef: React.RefObject<HTMLDivElement>;
}

const DifferenceListComponent = ({
  zoom,
  detailedDiff,
  detailedDoc,
  setDetailedDiff,
  scale,
  detailedDocId,
  setScale,
  images,
  rotate,
  onLoad,
  side,
  docContRef,
}: IDifferenceListProps) => {
  return images.map((diff, index) => {
    let firstDetailedDiff: string = "0";
    let newDiffs: IDifference[] = [];

    detailedDoc &&
      detailedDoc.docflowDocumentData &&
      detailedDoc.docflowDocumentData.extendData &&
      detailedDoc.docflowDocumentData.extendData.discrepancyData.forEach(
        (element, indexDiff) => {
          if (element.details) {
            element.details.forEach((detail) => {
              if (
                detail.documentPageIndex === index &&
                detail.documentId === detailedDocId
              ) {
                const isPinned =
                  element.status.length === 1 && element.status[0].pinned;
                newDiffs.push({
                  ...detail,
                  apiIndex: element.apiIndex,
                  discrepancyIndex: indexDiff,
                  isPinned,
                  type: element.type,
                });
              }
            });
          }
        }
      );

    return (
      <DifferenceDocument
        setScale={setScale}
        imageSrc={diff.fileUrl}
        rotate={rotate}
        zoom={zoom}
        scale={scale}
        items={newDiffs}
        pageIndex={index}
        side={side}
        firstDetailedDiff={firstDetailedDiff}
        setDetailedDiff={setDetailedDiff}
        detailedDiff={detailedDiff}
        onLoad={onLoad}
        docContRef={docContRef}
      />
    );
  });
};

//@ts-ignore
const DifferenceDocumentList = React.memo(DifferenceListComponent);

export type DifferencePageProps = IDifferencePageStateProps &
  IDifferencePageDispatchProps;

export interface IDifferenceDocumentPageState {
  scaleLeft: number;
  scaleRight: number;
  zoomLeft: number;
  zoomRight: number;
  rotateLeft: number;
  rotateRight: number;
  mouseRight: boolean;
  mouseLeft: boolean;
  comment: string;
  isRightPanelCollapsed: boolean;
}

const initialState: IDifferenceDocumentPageState = {
  scaleLeft: 0,
  scaleRight: 0,
  zoomLeft: 1,
  zoomRight: 1,
  rotateLeft: 0,
  rotateRight: 0,
  mouseRight: false,
  mouseLeft: false,
  comment: "",
  isRightPanelCollapsed: false,
};

export interface IDocumentButton {
  className: string;
  title: string;
  onClickLeft: () => void;
  onClickRight: () => void;
}

export const RightPannelContext = React.createContext({
  isRightPanelCollapsed: false,
});

export function DifferencePage(props: DifferencePageProps) {
  const [state, setState] = useState({ ...initialState });
  const isActInfoConditional = useSelector(getActInfoConditional);
  const discrepancyFilters = useSelector(selectDiscrepancyFilters);
  const {
    comment,
    imagesLeft,
    imagesRight,
    detailedDocLeftId,
    detailedDocRightId,
    detailedDocLeft,
    detailedDocRight,
  } = props;
  let icon;
  switch (props.docStatus) {
    case MonitoringStatuses.ACTS_NOT_RECOGNISED:
      icon = styles.actsNotRecognized;
      break;
    case MonitoringStatuses.WAITING_FOR_PAIR:
      icon = styles.waitingForPair;
      break;
    case MonitoringStatuses.ACTS_REJECTED:
      icon = styles.actsRejected;
      break;
    case MonitoringStatuses.ACTS_PASSED:
      icon = styles.actPassed;
      break;
    case MonitoringStatuses.ACTS_MATCH:
      icon = styles.actsMatch;
      break;
    default:
      icon = styles.actsHaveDifference;
  }

  const onLeftDocLoad = useCallback(() => {
    props.setLeftScale(0);
    setState((prevState) => ({
      ...prevState,
      scaleLeft: 1,
      rotateLeft: 0,
    }));
  }, []);

  const onRightDocLoad = useCallback(() => {
    props.setRightScale(1);
    setState((prevState) => ({
      ...prevState,
      scaleRight: 1,
      rotateRight: 0,
    }));
  }, []);

  const onSetLeftScale = useCallback((value: number) => {
    props.setLeftScale(value);
  }, []);

  const onSetRightScale = useCallback((value: number) => {
    props.setRightScale(value);
  }, []);

  function preventScroll(e) {
    e.preventDefault();
    return false;
  }

  useEffect(() => {
    setState((prevState) => ({ ...prevState, comment: props.comment }));
  }, [comment]);

  useLayoutEffect(() => {
    setTimeout(() => {
      const elementsScroll = [
        document.getElementById("documentsContainerLeft"),
        document.getElementById("documentsContainerLeft"),
      ];
      const leftScroll = elementsScroll[0];
      const rightScroll = elementsScroll[1];
      if (elementsScroll[0] && elementsScroll[1]) {
        // @ts-ignore
        refLeft.current.addEventListener("wheel", preventScroll, {
          passive: false,
        });
        // @ts-ignore
        refLeft.current.addEventListener("mousewheel", preventScroll, {
          passive: false,
        });
        // @ts-ignore
        refRight.current.addEventListener("wheel", preventScroll, {
          passive: false,
        });
        // @ts-ignore
        refRight.current.addEventListener("mousewheel", preventScroll, {
          passive: false,
        });
        // @ts-ignore
        const zoomLeft = leftScroll.clientHeight / 950;
        const zoomRight = rightScroll.clientHeight / 950;
        setState((prevState) => ({
          ...prevState,
          zoomLeft,
          zoomRight,
        }));
      }
    }, 200);

    return () => {
      props.onClearDifferencePage();
    };
  }, []);

  useLayoutEffect(() => {
    setTimeout(() => {
      const elementsScroll = document.getElementsByClassName(
        "ScrollbarsCustom trackYVisible"
      );
      const leftScroll = elementsScroll[0];
      const rightScroll = elementsScroll[1];
      if (elementsScroll[0] && elementsScroll[1]) {
        const zoomLeft = leftScroll.clientHeight / 900;
        const zoomRight = rightScroll.clientHeight / 900;
        setState((prevState) => ({
          ...prevState,
          zoomLeft,
          zoomRight,
        }));
      }
    }, 200);
  }, [
    imagesLeft.length,
    imagesRight.length,
    detailedDocRightId,
    detailedDocLeftId,
  ]);

  const onSetDetailedDiff = useCallback((diff: string | undefined) => {
    if (diff) {
      props.setDetailedDiff(diff);
    }
  }, []);

  const onButtonSetRotate = (
    count: number,
    part: "rotateRight" | "rotateLeft"
  ) => {
    let prevRotate = state[part];
    prevRotate += count;
    if (prevRotate >= 360 || prevRotate <= -360) {
      prevRotate = 0;
    }
    setState((prevState) => ({ ...prevState, [part]: prevRotate }));
  };

  const refRight =
    useRef<HTMLDivElement>() as React.MutableRefObject<HTMLInputElement>;
  const refLeft =
    useRef<HTMLDivElement>() as React.MutableRefObject<HTMLInputElement>;
  const { events } = useDraggable(refLeft, {
    activeMouseButton: "Left",
  });

  const right = useDraggable(refRight, {
    activeMouseButton: "Left",
  });

  const toggleRightPanel = useCallback(() => {
    setState((prev) => ({
      ...prev,
      isRightPanelCollapsed: !prev.isRightPanelCollapsed,
    }));
  }, []);

  // Вычисляем количество скрытых расхождений
  const hiddenDiscrepancyCount = React.useMemo(() => {
    if (
      detailedDocLeft &&
      detailedDocLeft.docflowDocumentData.extendData &&
      detailedDocLeft.docflowDocumentData.extendData.discrepancyData
    ) {
      return detailedDocLeft.docflowDocumentData.extendData.discrepancyData.filter(
        (el) => !discrepancyFilters.includes(el.type)
      ).length;
    }
    return 0;
  }, [detailedDocLeft, discrepancyFilters]);

  return (
    <PageWrapper>
      <DocumentDifferenceLoaderConnected />
      <div id={"PageContainerLeft"} className={styles.pageContainer}>
        <div
          style={{
            width: state.isRightPanelCollapsed
              ? "calc(100% - 90px)"
              : "calc(100% - 240px)",
          }}
          className={styles.leftPart}
        >
          <div className={styles.iconContainer}>
            {icon && (
              <div
                style={{ visibility: props.docStatus ? "visible" : "hidden" }}
                title={props.title}
                className={icon}
              />
            )}
          </div>
          <div className={styles.rowItemsWrapper}>
            <div className={styles.rowItemContainer}>
              <div className={styles.titleContainer}>
                <div>
                  {props.detailedDocLeft && (
                    <span>{detailedDocLeft.docflowDocumentData.comment}</span>
                  )}
                </div>
                <div className={styles.title}>
                  {props.detailedDocLeft &&
                    props.detailedDocLeft.docflowDocumentData.extendData
                      .organization}
                </div>
                {isActInfoConditional && (
                  <>
                    <div style={{ textAlign: "center" }}>
                      {props.detailedDocLeft &&
                        detailedDocLeft.docflowDocumentData.extendData
                          .periodStart &&
                        detailedDocLeft.docflowDocumentData.extendData
                          .periodEnd && (
                          <span>
                            {`${moment(
                              detailedDocLeft.docflowDocumentData.extendData
                                .periodStart
                            ).format("DD.MM.YYYY")}-${moment(
                              detailedDocLeft.docflowDocumentData.extendData
                                .periodEnd
                            ).format("DD.MM.YYYY")}`}
                          </span>
                        )}
                    </div>
                    <div>
                      {props.detailedDocLeft && (
                        <span>
                          {detailedDocLeft.docflowDocumentData.extendData
                            .agreement || "По нескольким договорам"}
                        </span>
                      )}
                    </div>
                  </>
                )}
              </div>
              <div
                id={"documentsContainerLeft"}
                {...events}
                ref={refLeft}
                className={`${styles.documentsContainer} ${
                  props.leftScale <= 1 && styles.centering
                }`}
              >
                {props.imagesLeft.length > 0 && (
                  <DifferenceDocumentList
                    images={props.imagesLeft}
                    detailedDoc={detailedDocLeft}
                    detailedDocId={detailedDocLeftId}
                    setScale={onSetLeftScale}
                    rotate={state.rotateLeft}
                    zoom={state.zoomLeft}
                    scale={props.leftScale}
                    side={PageParts.Left}
                    setDetailedDiff={onSetDetailedDiff}
                    detailedDiff={props.detailedDiff}
                    onLoad={onLeftDocLoad}
                    docContRef={refLeft}
                  />
                )}
              </div>
              <div className={styles.documentButtonsContainer}>
                <IconButton
                  title={"Вписать"}
                  className={styles.scaleIcon}
                  onClick={() => {
                    props.setLeftScale(1);
                    setState((prevState) => ({
                      ...prevState,
                      scaleLeft: 1,
                      rotateLeft: 0,
                    }));
                  }}
                />
                <IconButton
                  title={"Уменьшить"}
                  onClick={() => {
                    if (props.leftScale < 1.2) {
                      return;
                    }
                    props.setLeftScale(props.leftScale - 0.4);

                    const ratio = (props.leftScale - 0.4) / props.leftScale;
                    const pagesCont = document.getElementById("pagesContainer");
                    const docCont: any = refLeft?.current;
                    const offsetTop =
                      docCont.clientHeight / 2 - pagesCont.offsetTop;
                    docCont.scrollLeft =
                      (docCont.scrollLeft + docCont.clientWidth / 2) * ratio -
                      docCont.clientWidth / 2;
                    docCont.scrollTop =
                      (docCont.scrollTop + offsetTop) * ratio - offsetTop;
                  }}
                  className={styles.zoomOutIcon}
                />
                <IconButton
                  title={"Увеличить"}
                  onClick={() => {
                    props.setLeftScale(props.leftScale + 0.4);

                    const ratio = (props.leftScale + 0.4) / props.leftScale;
                    const pagesCont = document.getElementById("pagesContainer");
                    const docCont: any = refLeft?.current;
                    const offsetTop =
                      docCont.clientHeight / 2 - pagesCont.offsetTop;
                    docCont.scrollLeft =
                      (docCont.scrollLeft + docCont.clientWidth / 2) * ratio -
                      docCont.clientWidth / 2;
                    docCont.scrollTop =
                      (docCont.scrollTop + offsetTop) * ratio - offsetTop;
                  }}
                  className={styles.zoomInIcon}
                />
                <IconButton
                  title={"Повернуть влево"}
                  onClick={() => onButtonSetRotate(-90, "rotateLeft")}
                  className={styles.rotateLeftIcon}
                />
                <IconButton
                  title={"Повернуть вправо"}
                  onClick={() => onButtonSetRotate(90, "rotateLeft")}
                  className={styles.rotateRightIcon}
                />
              </div>
            </div>
            <div id="rowItemContainerRight" className={styles.rowItemContainer}>
              <div className={styles.titleContainer}>
                <div>
                  {props.detailedDocRight && (
                    <span>{detailedDocRight.docflowDocumentData.comment}</span>
                  )}
                </div>
                <div
                  title={
                    props.detailedDocRight
                      ? props.detailedDocRight.docflowDocumentData.extendData
                          .organization
                      : ""
                  }
                  className={styles.title}
                >
                  {props.detailedDocRight &&
                    truncate(
                      props.detailedDocRight.docflowDocumentData.extendData
                        .organization,
                      44
                    )}
                </div>
                <div>
                  {isActInfoConditional && (
                    <>
                      <div style={{ textAlign: "center" }}>
                        {props.detailedDocRight &&
                          detailedDocRight.docflowDocumentData.extendData
                            .periodStart &&
                          detailedDocRight.docflowDocumentData.extendData
                            .periodEnd && (
                            <span>
                              {`${moment(
                                detailedDocRight.docflowDocumentData.extendData
                                  .periodStart
                              ).format("DD.MM.YYYY")}-${moment(
                                detailedDocRight.docflowDocumentData.extendData
                                  .periodEnd
                              ).format("DD.MM.YYYY")}`}
                            </span>
                          )}
                      </div>
                      <div>
                        {props.detailedDocRight && (
                          <span>
                            {detailedDocRight.docflowDocumentData.extendData
                              .agreement || "По нескольким договорам"}
                          </span>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
              <div
                id={"documentsContainerRight"}
                {...right.events}
                ref={refRight}
                className={`${styles.documentsContainer} ${
                  props.rightScale <= 1 && styles.centering
                }`}
              >
                {props.imagesRight.length > 0 && (
                  <DifferenceDocumentList
                    images={props.imagesRight}
                    detailedDoc={detailedDocRight}
                    detailedDocId={detailedDocRightId}
                    setScale={onSetRightScale}
                    rotate={state.rotateRight}
                    zoom={state.zoomRight}
                    scale={props.rightScale}
                    side={PageParts.Right}
                    setDetailedDiff={onSetDetailedDiff}
                    detailedDiff={props.detailedDiff}
                    onLoad={onRightDocLoad}
                    docContRef={refRight}
                  />
                )}
              </div>
              <div className={styles.documentButtonsContainer}>
                <IconButton
                  title={"Вписать"}
                  className={styles.scaleIcon}
                  onClick={() => {
                    props.setRightScale(1);
                    setState((prevState) => ({
                      ...prevState,
                      scaleRight: 1,
                      rotateRight: 0,
                    }));
                  }}
                />
                <IconButton
                  title={"Уменьшить"}
                  onClick={() => {
                    if (props.rightScale < 1.2) {
                      return;
                    }
                    props.setRightScale(props.rightScale - 0.4);

                    const ratio = (props.rightScale - 0.4) / props.rightScale;
                    const pagesCont = document.getElementById("pagesContainer");
                    const docCont: any = refRight?.current;
                    const offsetTop =
                      docCont.clientHeight / 2 - pagesCont.offsetTop;
                    docCont.scrollLeft =
                      (docCont.scrollLeft + docCont.clientWidth / 2) * ratio -
                      docCont.clientWidth / 2;
                    docCont.scrollTop =
                      (docCont.scrollTop + offsetTop) * ratio - offsetTop;
                  }}
                  className={styles.zoomOutIcon}
                />
                <IconButton
                  title={"Увеличить"}
                  onClick={() => {
                    props.setRightScale(props.rightScale + 0.4);

                    const ratio = (props.rightScale + 0.4) / props.rightScale;
                    const pagesCont = document.getElementById("pagesContainer");
                    const docCont: any = refRight?.current;
                    const offsetTop =
                      docCont.clientHeight / 2 - pagesCont.offsetTop;
                    docCont.scrollLeft =
                      (docCont.scrollLeft + docCont.clientWidth / 2) * ratio -
                      docCont.clientWidth / 2;
                    docCont.scrollTop =
                      (docCont.scrollTop + offsetTop) * ratio - offsetTop;
                  }}
                  className={styles.zoomInIcon}
                />
                <IconButton
                  title={"Повернуть влево"}
                  onClick={() => onButtonSetRotate(-90, "rotateRight")}
                  className={styles.rotateLeftIcon}
                />
                <IconButton
                  title={"Повернуть вправо"}
                  onClick={() => onButtonSetRotate(90, "rotateRight")}
                  className={styles.rotateRightIcon}
                />
              </div>
            </div>
          </div>
        </div>
        <div
          className={`${styles.rightPart} ${
            state.isRightPanelCollapsed ? styles.collapsedRightPart : ""
          }`}
        >
          <div className={styles.rightPartContent}>
            {!state.isRightPanelCollapsed && (
              <div className={styles.titleRow}>
                <div className={`${styles.title} ${styles.titleNoMargin}`}>
                  {props.detailedDocLeft &&
                  props.detailedDocLeft.docflowDocumentData.extendData
                    .discrepancyData.length > 0
                    ? hiddenDiscrepancyCount > 0
                      ? `еще ${hiddenDiscrepancyCount} расхождений скрыто фильтрами`
                      : ""
                    : props.title
                    ? props.title
                    : ""}
                </div>
              </div>
            )}
            <div
              className={`${styles.commentsContainer} ${styles.commentsEmptyContainer}`}
            >
              <Scrollbar width={"100%"} height={"80%"}>
                <RightPannelContext.Provider
                  value={{ isRightPanelCollapsed: state.isRightPanelCollapsed }}
                >
                  <DifferenceListConnected />
                </RightPannelContext.Provider>
              </Scrollbar>
            </div>
            <div className={styles.buttonsContainer}>
              <StyledEngineProvider injectFirst>
                <Button
                  onClick={toggleRightPanel}
                  className={styles.collapsedBtn}
                >
                  <DoubleArrowIcon
                    sx={{
                      color: "#000",
                      width: "100%",
                      rotate: state.isRightPanelCollapsed ? "180deg" : "0deg",
                    }}
                  />
                </Button>
              </StyledEngineProvider>
            </div>
          </div>
        </div>
      </div>
    </PageWrapper>
  );
}
